import { defineStore } from 'pinia';
import { ref } from 'vue';

import type { SeenByUserDTO } from '@nora-monorepo/nora-api-client';
import { appLogger, useApiStore } from '@nora-monorepo/ui-shared';

export const useSeenByModalStore = defineStore('seenByModalStore', () => {
  const isLoading = ref(true);
  const hasError = ref(false);
  const seenByUsers = ref<SeenByUserDTO[]>([]);
  const api = useApiStore();

  const messageId = ref<string | null>(null);
  const conversationId = ref<string | null>(null);
  const isOpen = ref(false);

  // Bumped on every open and reset, so a stale lookup cannot overwrite a newer one.
  let latestRequestId = 0;

  async function openModal(
    selectedMessageId: string,
    selectedConversationId: string,
  ): Promise<void> {
    const requestId = ++latestRequestId;

    isLoading.value = true;
    seenByUsers.value = [];
    hasError.value = false;

    isOpen.value = true;
    messageId.value = selectedMessageId;
    conversationId.value = selectedConversationId;

    try {
      const result = await api.unwrap(
        api.conversationMessage.getMessageSeenBy({
          messageId: messageId.value,
          conversationId: conversationId.value,
        }),
      );

      if (requestId !== latestRequestId) {
        return;
      }

      seenByUsers.value = result.data ?? [];
    }
    catch (error) {
      if (requestId !== latestRequestId) {
        return;
      }

      appLogger.error({
        message: 'Could not load message readers',
        context: {
          conversationId: selectedConversationId,
          messageId: selectedMessageId,
        },
        sensitiveContext: { error },
      });

      hasError.value = true;
    }
    finally {
      if (requestId === latestRequestId) {
        isLoading.value = false;
      }
    }
  }

  function reset(): void {
    latestRequestId++;

    isLoading.value = false;
    hasError.value = false;
    seenByUsers.value = [];

    messageId.value = null;
    conversationId.value = null;
    isOpen.value = false;
  }

  return {
    isLoading,
    hasError,
    seenByUsers,
    isOpen,

    openModal,
    reset,
  };
});
